import React from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import {
  getEffectiveChecklistStatus,
  isRowApproved,
  isRowManuallyApproved,
  isRowManuallyRequired,
} from '../../manualOverridePersistence';
import ApprovalActionButton from '../../reportCards/ApprovalActionButton';
import ApprovalStatusChip from '../../reportCards/ApprovalStatusChip';
import ChecklistStatusDot from '../../reportCards/ChecklistStatusDot';
import EmptyTableRow from '../../reportCards/EmptyTableRow';
import HiddenApprovedFooter from '../../reportCards/HiddenApprovedFooter';
import ReportCard from '../../reportCards/ReportCard';
import ReportNote from '../../reportCards/ReportNote';
import SortTableCell from '../../reportCards/SortTableCell';
import { type MarginBand, formatMoney } from '../../shared/checklistUtils';
import type { ManualOverrideState } from '../../types';

import DetailField from './DetailField';
import { EGS_TABLE_COLUMN_WIDTHS } from './constants';
import type { EgsMarginChecklistRow } from './types';

const COLUMN_COUNT = 9;

interface Props {
  expandedRows: Record<string, boolean>;
  hiddenApprovedCount: number;
  manualOverrides: ManualOverrideState;
  rows: Array<EgsMarginChecklistRow>;
  showAutoApproved: boolean;
  onApprovalChange: (rowId: string) => void;
  onToggleDetails: (rowId: string, defaultOpen?: boolean) => void;
  onUnapprovalChange: (rowId: string) => void;
}

export default function MarginReportCard({
  expandedRows,
  hiddenApprovedCount,
  manualOverrides,
  rows,
  showAutoApproved,
  onApprovalChange,
  onToggleDetails,
  onUnapprovalChange,
}: Props) {
  return (
    <ReportCard>
      <ReportNote
        icon={<InfoOutlinedIcon />}
        text={
          <>
            <strong>Extra Guest Surcharges margin check:</strong>{' '}
            <Box component="span" sx={{ color: 'error.main', fontWeight: 700 }}>
              red
            </Box>{' '}
            is <strong>10% or below</strong>,{' '}
            <Box component="span" sx={{ color: 'warning.main', fontWeight: 700 }}>
              yellow
            </Box>{' '}
            is <strong>above 10% up to 18%</strong>, and{' '}
            <Box component="span" sx={{ color: 'success.main', fontWeight: 700 }}>
              green
            </Box>{' '}
            is <strong>19%+</strong>.
          </>
        }
      />
      <TableContainer>
        <Table size="small" sx={{ minWidth: 1108, tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <SortTableCell sx={EGS_TABLE_COLUMN_WIDTHS.name}>Room</SortTableCell>
              <SortTableCell sx={{ width: EGS_TABLE_COLUMN_WIDTHS.col2 }} labelSx={{ whiteSpace: 'normal' }}>
                Extra Guest Type
              </SortTableCell>
              <SortTableCell sx={{ width: EGS_TABLE_COLUMN_WIDTHS.col3 }} labelSx={{ whiteSpace: 'normal' }}>
                Age Range
              </SortTableCell>
              <SortTableCell sx={{ width: EGS_TABLE_COLUMN_WIDTHS.col4 }} labelSx={{ whiteSpace: 'normal' }}>
                Cost Price
              </SortTableCell>
              <SortTableCell sx={{ width: EGS_TABLE_COLUMN_WIDTHS.col5 }} labelSx={{ whiteSpace: 'normal' }}>
                Sell Price
              </SortTableCell>
              <SortTableCell sx={{ width: EGS_TABLE_COLUMN_WIDTHS.col6 }} labelSx={{ whiteSpace: 'normal' }}>
                % Margin
              </SortTableCell>
              <SortTableCell sx={{ width: EGS_TABLE_COLUMN_WIDTHS.check }} labelSx={{ whiteSpace: 'normal' }}>
                Margin Check
              </SortTableCell>
              <TableCell sx={{ width: EGS_TABLE_COLUMN_WIDTHS.action }}>Action</TableCell>
              <TableCell sx={{ width: EGS_TABLE_COLUMN_WIDTHS.chevron }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <EmptyTableRow colSpan={COLUMN_COUNT} label="No EGS rows match this view." />
            ) : (
              rows.map((row) => {
                const manuallyApproved = isRowManuallyApproved(row, manualOverrides);
                const manuallyRequired = isRowManuallyRequired(row, manualOverrides);
                const approved = isRowApproved(row, manualOverrides);
                const hasDetails = !!(row.beddingType || row.inclusions || row.collectedBy);
                const detailsOpen = !!expandedRows[row.id];

                return (
                  <React.Fragment key={row.id}>
                    <TableRow
                      hover
                      onClick={hasDetails ? () => onToggleDetails(row.id, false) : undefined}
                      sx={{ cursor: hasDetails ? 'pointer' : 'default', '& td': { py: 1.5 } }}
                    >
                      <TableCell sx={EGS_TABLE_COLUMN_WIDTHS.name}>
                        <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5, overflowWrap: 'break-word' }}>
                          {row.name}
                        </Typography>
                        <ApprovalStatusChip approved={approved} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{row.extraGuestType || 'N/A'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{row.ageRange || 'N/A'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatMoney(row.costPrice, row.currencyCode)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatMoney(row.sellPrice, row.currencyCode)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {typeof row.margin === 'number' ? `${row.margin.toFixed(1)}%` : 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <ChecklistStatusDot
                          approvalType={manuallyApproved ? 'manual' : 'auto'}
                          status={getEffectiveChecklistStatus(row, manualOverrides)}
                          band={getMarginStatusBand({ approved, manuallyRequired, marginBand: row.marginBand })}
                          hideLabel
                        />
                      </TableCell>
                      <TableCell onClick={(event) => event.stopPropagation()}>
                        <ApprovalActionButton
                          approved={approved}
                          onApprove={() => onApprovalChange(row.id)}
                          onUnapprove={() => onUnapprovalChange(row.id)}
                        />
                      </TableCell>
                      <TableCell>
                        {hasDetails && (
                          <IconButton size="small" aria-label={detailsOpen ? 'Hide details' : 'Show details'}>
                            <ExpandMoreIcon
                              fontSize="small"
                              sx={{ transform: detailsOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
                            />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                    {hasDetails && (
                      <TableRow>
                        <TableCell colSpan={COLUMN_COUNT} sx={{ p: 0, borderBottom: detailsOpen ? undefined : 'none' }}>
                          <Collapse in={detailsOpen} timeout="auto" unmountOnExit>
                            <Box
                              sx={{
                                bgcolor: 'action.hover',
                                borderLeft: 4,
                                borderLeftColor: 'divider',
                                py: 1.5,
                                pl: 3,
                                pr: 2,
                              }}
                            >
                              <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                                <DetailField label="Bedding type" value={row.beddingType} width={300} />
                                <DetailField label="Inclusions" value={row.inclusions} width={280} />
                                <DetailField label="Collected by" value={row.collectedBy} width={210} />
                              </Stack>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <HiddenApprovedFooter count={hiddenApprovedCount} noun="EGS row" showAutoApproved={showAutoApproved} />
    </ReportCard>
  );
}

function getMarginStatusBand({
  approved,
  manuallyRequired,
  marginBand,
}: {
  approved: boolean;
  manuallyRequired: boolean;
  marginBand?: MarginBand;
}) {
  if (approved) {
    return 'green';
  }

  if (manuallyRequired) {
    return undefined;
  }

  return marginBand;
}
